import { NavLink, Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  BellOutlined,
  MoonOutlined,
  SunOutlined,
  SwapOutlined,
  TableOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Button, Segmented, Space } from "antd";
import type { Language } from "../../types";
import { AppHeaderLogo } from "../../components/AppHeaderLogo";
import { GlobalUserMenu } from "../../components/GlobalUserMenu";
import { useReviewCredits } from "../../hooks/useReviewCredits";
import { useUiState } from "../../state";
import { textMap } from "../../i18n";
import "../../styles/studio-workbench.css";
import { STUDIO_MONITORING_EXPORT_EVENT } from "./studioConstants";

/**
 * /studio/new、/studio/scans、/studio/scans/:id 共用侧栏+顶栏；详情仅替换主内容区。
 */
export function StudioShell() {
  const { lang, themeMode, setLang, toggleTheme } = useUiState();
  const t = textMap[lang];
  const { reviewCredits, billingReady, isUnlimited } = useReviewCredits();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = themeMode === "dark";
  const scanDetailMatch = useMatch({ path: "/studio/scans/:scanId", end: true });
  const isMonitoringRoute = location.pathname === "/studio/monitoring";
  const isCompareRoute = location.pathname === "/studio/compare";

  const crumbMiddle = isMonitoringRoute
    ? t.studioCrumbMonitoring
    : isCompareRoute
      ? t.studioCrumbCompare
      : location.pathname === "/studio/scans" || scanDetailMatch
        ? t.studioCrumbRecords
        : t.studioCrumbScan;

  return (
    <div className={`studio-wb-root ${isDark ? "studio-wb-dark" : ""}`}>
      <aside className="studio-wb-sidenav">
        <div className="studio-wb-brand">
          <div>
            <div className="studio-wb-brand-title">XTool Studio</div>
            <div className="studio-wb-brand-sub">{t.studioBrandSub}</div>
          </div>
        </div>
        <nav className="studio-wb-nav">
          <NavLink
            to="/studio/new"
            className={({ isActive }) =>
              `studio-wb-nav-item${isActive ? " active" : ""}`
            }
          >
            <ThunderboltOutlined />
            {t.studioNavNewScan}
          </NavLink>
          <NavLink
            to="/studio/scans"
            className={({ isActive }) =>
              `studio-wb-nav-item${isActive ? " active" : ""}`
            }
          >
            <TableOutlined />
            {t.studioNavRecords}
          </NavLink>
          <NavLink
            to="/studio/compare"
            className={({ isActive }) =>
              `studio-wb-nav-item${isActive ? " active" : ""}`
            }
          >
            <SwapOutlined />
            {t.studioNavCompare}
          </NavLink>
          <NavLink
            to="/studio/monitoring"
            end
            className={({ isActive }) =>
              `studio-wb-nav-item${isActive ? " active" : ""}`
            }
          >
            <BellOutlined />
            {t.studioNavMonitoring}
          </NavLink>
        </nav>
        <div className="studio-wb-sidenav-foot">
          <button
            type="button"
            className="studio-wb-nav-item"
            onClick={() => navigate("/chat")}
          >
            <ArrowLeftOutlined />
            {t.studioBackToChat}
          </button>
        </div>
      </aside>

      <div className="studio-wb-main">
        <header className="studio-wb-topbar">
          <div className="studio-wb-topbar-left">
            <AppHeaderLogo className="studio-wb-topbar-logo" />
            <div className="studio-wb-crumb">
            <span className="studio-wb-crumb-muted">Studio</span>
            <span className="studio-wb-crumb-muted">/</span>
            {scanDetailMatch ? (
              <>
                <button
                  type="button"
                  className="studio-wb-crumb-link"
                  onClick={() => navigate("/studio/scans")}
                >
                  {t.studioCrumbRecords}
                </button>
                <span className="studio-wb-crumb-muted">/</span>
                <span className="studio-wb-crumb-active">{t.studioCrumbDetail}</span>
              </>
            ) : (
              <span className="studio-wb-crumb-active">{crumbMiddle}</span>
            )}
            </div>
          </div>
          <Space size={12} align="center">
            {isMonitoringRoute ? (
              <Button
                type="primary"
                onClick={() =>
                  window.dispatchEvent(new Event(STUDIO_MONITORING_EXPORT_EVENT))
                }
              >
                {t.studioMonGenReport}
              </Button>
            ) : null}
            {billingReady ? (
              <div className="studio-wb-topbar-credits" title={t.creditsLabel}>
                {isUnlimited
                  ? t.studioTopbarScansUnlimited
                  : t.studioTopbarScansRemainingTpl.replace(
                      "{n}",
                      reviewCredits.toLocaleString(),
                    )}
              </div>
            ) : null}
            <Segmented
              className="lang-bubble"
              value={lang}
              options={[
                { label: "EN", value: "en" },
                { label: "中文", value: "zh" },
              ]}
              onChange={(v) => setLang(v as Language)}
            />
            <Button
              className="icon-btn-pill icon-btn-pill-circle"
              icon={isDark ? <SunOutlined /> : <MoonOutlined />}
              onClick={() => toggleTheme(!isDark)}
            />
            <GlobalUserMenu />
          </Space>
        </header>

        <div className="studio-wb-body">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
